/**
 * MKS-315 — the keyboard shortcuts used to sit under the grid as a permanent
 * line of pipe-separated text, competing for attention with the numbers above
 * it. Same content, now behind one small keyboard button, so the page stays
 * quiet until somebody asks for the list.
 *
 * Heroicons has no keyboard glyph (v2 ships `CommandLineIcon`, a terminal
 * prompt — the wrong thing to point at here), so the icon is inline.
 */
import { useEffect, useRef, useState } from 'react';

export interface Shortcut {
  /** The keys, rendered monospaced — e.g. `Shift+←/→`. */
  keys: string;
  /** What they do — e.g. `Navigate months`. */
  action: string;
}

function KeyboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <rect x="2.25" y="5.25" width="19.5" height="13.5" rx="2" strokeLinejoin="round" />
      <path
        strokeLinecap="round"
        d="M6 9h.01M9.5 9h.01M13 9h.01M16.5 9h.01M6 12.5h.01M9.5 12.5h.01M13 12.5h.01M16.5 12.5h.01M8 15.75h8"
      />
    </svg>
  );
}

export function ShortcutsPopover({ shortcuts }: { shortcuts: Shortcut[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <KeyboardIcon className="w-4 h-4" />
        Shortcuts
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Keyboard shortcuts"
          className="absolute right-0 z-40 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
        >
          <dl className="divide-y divide-gray-100">
            {shortcuts.map((shortcut) => (
              <div key={shortcut.action} className="flex items-center justify-between gap-3 py-1.5">
                <dt className="text-xs text-gray-600">{shortcut.action}</dt>
                <dd className="font-mono text-[11px] text-gray-900 whitespace-nowrap">
                  {shortcut.keys}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
